/**
 * Created by katarinababic on 21.11.24.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { KinasticAudioPlayer } from '../KinasticAudioPlayer';
import { useRootStore } from '../../../Store/useRootStore';
import { MeditationEntry } from '../../../Model/Explore/MeditationEntry';
import { useLocalizedImage } from '../../../Hooks/useLocalizedMedia';
import { getMediaLargeOrMediumOrSmallestUriObject } from '../../../Model/Media/Media';
import { saveActivityLogDirect } from '../../../Model/Activity/ActivityLog';
import { useLocalized } from '../../../Hooks/useLocalized';
import { Waveform } from '../MeditationBackground/Waveform';
import dayjs from 'dayjs';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Container = styled.div`
  position: relative;
  width: 100%;
  min-height: 75vh;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.25);
  animation: ${fadeIn} 0.6s ease-out;

  @media (max-width: 768px) {
    min-height: 80vh;
    border-radius: 20px;
  }
`;

const PlayerWrapper = styled.div`
  position: absolute;
  inset: 0;
`;

const ContentOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 5;
  background: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.35) 0%, rgba(0, 0, 0, 0.15) 60%, transparent 100%);
`;

const TitleArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
`;

const EntryTitle = styled.h2`
  font-size: 36px;
  font-weight: 400;
  color: white;
  text-align: center;
  margin: 0;
  letter-spacing: 2px;
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.7), 0 0 40px rgba(0, 0, 0, 0.4);
  max-width: 600px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 26px;
    letter-spacing: 1px;
    max-width: 340px;
  }
`;

const NarratorName = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  text-transform: uppercase;
  letter-spacing: 4px;
  text-shadow: 0 1px 12px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    font-size: 13px;
    letter-spacing: 3px;
  }
`;

const WaveformArea = styled.div`
  position: relative;
  width: 100%;
  max-width: 600px;
  height: 160px;

  @media (max-width: 768px) {
    max-width: 360px;
    height: 120px;
  }
`;

export type ExploreContentViewMeditationPlayerProps = {
  entry: MeditationEntry;
};

export const ExploreContentViewMeditationPlayer: React.FC<ExploreContentViewMeditationPlayerProps> = observer(({ entry }) => {
  const { engagement, authentication, analytics } = useRootStore();
  const [isPlaying, setIsPlaying] = React.useState(false);

  const title = useLocalized(entry.name);
  const narrator = entry.narrators[0];
  const narratorName = narrator?.name;

  // Collect all audio tracks from all narrators, setting language on each Media object
  const audioTracks = React.useMemo(() => {
    return entry.narrators.flatMap((narrator) =>
      narrator.audioTracks.map((track) => {
        const media = track.media;
        if (!media.language) {
          media.language = track.language;
        }
        return media;
      })
    );
  }, [entry.narrators]);
  const entryImage = useLocalizedImage(entry?.images);
  const backgroundImage = entryImage ? getMediaLargeOrMediumOrSmallestUriObject(entryImage).uri : undefined;

  const handleCreateActivityLog = React.useCallback(
    (watchedDurationInSeconds: number, totalDurationInSeconds: number) => {
      const { athlete } = authentication;

      const percentageCompleted = Math.min(1, watchedDurationInSeconds / totalDurationInSeconds);

      if (athlete && watchedDurationInSeconds > 0) {
        const activityLog = entry.createActivityLog(
          dayjs().subtract(watchedDurationInSeconds, 'seconds').toDate(),
          new Date(),
          {
            DURATION: watchedDurationInSeconds,
            PERCENTAGE_COMPLETED: percentageCompleted,
          },
        );

        engagement
          .sendActivityLogged(activityLog)
          .then((res) => {
            engagement.showPointsModalPromise(res).catch((error) => {
              console.error('Failed to show points modal:', error);
            });
            return saveActivityLogDirect(activityLog);
          })
          .then(() => {
            analytics.logEvents(['meditationSession_finished', 'explore_session_finished'], {
              id: entry.id,
              type: entry.type,
              totalDuration: totalDurationInSeconds,
              durationInSeconds: watchedDurationInSeconds,
              percentageCompleted,
              timestamp: Date.now(),
              name: entry.defaultName,
            });
          })
          .catch((error) => {
            console.error('Error in activity log processing:', error);
          });
      }
    },
    [authentication, engagement, entry, analytics],
  );

  return (
    <Container>
      <PlayerWrapper>
        <KinasticAudioPlayer
          audioTracks={audioTracks}
          title={title}
          entryId={entry.id}
          backgroundImage={backgroundImage}
          onWatchDuration={handleCreateActivityLog}
          onPlayStateChange={setIsPlaying}
          analyticsConfig={{
            contentId: entry.id,
            contentType: 'audio',
            mediaType: 'meditation',
            contentName: entry.defaultName,
          }}
        />
      </PlayerWrapper>

      <ContentOverlay>
        <TitleArea>
          {narratorName && <NarratorName>{narratorName}</NarratorName>}
          <EntryTitle>{title}</EntryTitle>
        </TitleArea>
        <WaveformArea>
          <Waveform isPlaying={isPlaying} barCount={40} />
        </WaveformArea>
      </ContentOverlay>
    </Container>
  );
});
